import React from "react";
import { Link } from "react-router-dom";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Header from "./Header/Header";
import Footer from "./Footer/Footer";

// Página comercial: beneficios para quien usa la plataforma, sin detalles
// técnicos. Cada afirmación corresponde a algo que la app ya hace.
const ANALYSIS_MODES = [
  {
    Icon: ChatBubbleOutlineIcon,
    title: "Análisis Básico",
    text: "Haz una pregunta como se la harías a un colega y recibe la respuesta en segundos, con su gráfica lista para presentar.",
    example: "“¿Cuánto vendimos por sucursal este mes?”"
  },
  {
    Icon: TravelExploreIcon,
    title: "Análisis Profundo",
    text: "Para las preguntas difíciles. WhisperBI investiga desde varios ángulos y te entrega un informe con conclusiones, hallazgos y las siguientes preguntas que vale la pena hacer.",
    example: "“¿Por qué bajaron las ventas en el norte?”"
  },
  {
    Icon: DashboardCustomizeIcon,
    title: "Dashboard",
    text: "Pide un tablero y obtenlo completo en un momento. Guárdalo, ordénalo a tu gusto y actualízalo cuando quieras.",
    example: "“Arma un tablero de ventas del trimestre.”"
  }
];

const BENEFITS = [
  {
    Icon: CloudUploadIcon,
    title: "Tus datos, donde estén",
    text: "Conecta tus bases de datos o simplemente sube un Excel o un CSV. Si tu archivo tiene varias hojas, WhisperBI encuentra cómo se relacionan."
  },
  {
    Icon: InsertChartOutlinedIcon,
    title: "Gráficas sin esfuerzo",
    text: "Cada respuesta con datos llega con la gráfica que mejor la explica. Sin fórmulas ni configuraciones."
  },
  {
    Icon: AutoAwesomeIcon,
    title: "Elige tu inteligencia",
    text: "Usa el modelo de IA que mejor se ajuste a cada pregunta: rápido para el día a día o más potente para el análisis fino."
  },
  {
    Icon: PhoneIphoneIcon,
    title: "Contigo en el celular",
    text: "Pregunta desde cualquier dispositivo. Si cambias de app o se corta la conexión, tu respuesta te espera."
  },
  {
    Icon: SavingsOutlinedIcon,
    title: "Costos claros",
    text: "Sabes cuánto consume cada pregunta y cuánto cuesta en promedio cada tipo de análisis. Empieza gratis y crece a tu ritmo."
  },
  {
    Icon: VerifiedUserOutlinedIcon,
    title: "Seguro por diseño",
    text: "Cada persona ve solo lo que le corresponde. Protege tu cuenta con verificación en dos pasos y revisa el historial de consultas."
  },
  {
    Icon: GroupsOutlinedIcon,
    title: "Hecho para equipos",
    text: "Invita a tu equipo, organiza espacios de trabajo por área y decide quién accede a cada información."
  }
];

const STEPS = [
  { title: "Conecta", text: "Vincula tus datos o sube tus archivos en unos minutos." },
  { title: "Pregunta", text: "Escribe lo que necesitas saber, en tus propias palabras." },
  { title: "Decide", text: "Recibe respuestas, gráficas y tableros listos para actuar." }
];

const FAQS = [
  {
    q: "¿Necesito saber programar o de análisis de datos?",
    a: "No. Si sabes hacer una pregunta, sabes usar WhisperBI. Escribes como hablas y la plataforma se encarga del resto."
  },
  {
    q: "¿Qué información puedo analizar?",
    a: "La de tus sistemas de negocio y bases de datos, o la de tus hojas de cálculo. Puedes empezar subiendo un Excel o un CSV."
  },
  {
    q: "¿Quién puede ver mis datos?",
    a: "Solo las personas de tu equipo a las que les des acceso. Tú decides qué ve cada quien, y puedes revisar las consultas que se hicieron."
  },
  {
    q: "¿Cuál es la diferencia entre los tres tipos de análisis?",
    a: "El Básico responde al momento, el Profundo investiga y te entrega un informe, y el Dashboard arma un tablero completo que puedes guardar y actualizar."
  },
  {
    q: "¿Cuánto cuesta?",
    a: "Puedes empezar gratis. Cada plan incluye créditos mensuales y siempre ves cuánto consume cada pregunta, sin sorpresas."
  },
  {
    q: "¿Puedo usarlo desde el celular?",
    a: "Sí. WhisperBI funciona en computadoras, tabletas y teléfonos."
  }
];

const primaryCta =
  "inline-block bg-orange-500 text-black font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-300 transition";
const secondaryCta =
  "inline-block bg-black text-white border-2 border-orange-500 px-6 py-3 rounded-full hover:bg-orange-500/10 focus:outline-none focus:ring-2 focus:ring-orange-500 transition";

const Home = () => (
  <div className="flex flex-col min-h-screen bg-black text-white">
    <Header />
    <main className="flex-grow">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 to-black py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-orange-400 font-semibold mb-3">Inteligencia de negocio con IA</p>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
              Pregúntale a tus datos. Decide con confianza.
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              WhisperBI convierte tus preguntas en respuestas claras, gráficas y tableros al
              instante. Sin fórmulas, sin esperar reportes y sin depender de nadie.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" className={primaryCta}>Empieza gratis</Link>
              <Link to="/precios" className={secondaryCta}>Ver precios</Link>
            </div>
          </div>
          <div>
            <img
              src="/images/whisperbi_fondo_login.jpg"
              alt="Equipo analizando resultados con WhisperBI"
              className="rounded-2xl shadow-lg w-full"
            />
          </div>
        </div>
      </section>

      {/* Tres formas de analizar */}
      <section className="py-16 px-4 bg-black" aria-labelledby="modos">
        <div className="max-w-6xl mx-auto">
          <h2 id="modos" className="text-3xl font-bold text-center mb-3">Tres formas de analizar</h2>
          <p className="text-gray-300 text-center mb-10 max-w-2xl mx-auto">
            Desde una respuesta rápida hasta un informe completo: elige la profundidad que necesitas en cada momento.
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {ANALYSIS_MODES.map(({ Icon, title, text, example }) => (
              <article key={title} className="bg-gray-900 border border-orange-500/40 rounded-2xl p-6 flex flex-col">
                <Icon sx={{ fontSize: 36, color: "#f97316" }} aria-hidden="true" />
                <h3 className="text-xl font-bold text-orange-500 mt-4 mb-2">{title}</h3>
                <p className="text-gray-300 flex-grow">{text}</p>
                <p className="text-gray-400 italic mt-4">{example}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-900" aria-labelledby="beneficios">
        <div className="max-w-6xl mx-auto">
          <h2 id="beneficios" className="text-3xl font-bold text-center mb-10">Todo lo que tu equipo necesita</h2>
          <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map(({ Icon, title, text }) => (
              <div key={title} className="flex gap-4">
                <Icon sx={{ fontSize: 30, color: "#f97316", flexShrink: 0 }} aria-hidden="true" />
                <div>
                  <h3 className="text-lg font-semibold mb-1">{title}</h3>
                  <p className="text-gray-300">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-16 px-4 bg-gray-900" aria-labelledby="como-funciona">
        <div className="max-w-5xl mx-auto">
          <h2 id="como-funciona" className="text-3xl font-bold text-center mb-10">Empieza en tres pasos</h2>
          <ol className="grid gap-8 md:grid-cols-3">
            {STEPS.map(({ title, text }, index) => (
              <li key={title} className="text-center">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-orange-500 text-orange-500 text-xl font-bold mb-4">
                  {index + 1}
                </span>
                <h3 className="text-xl font-semibold mb-2">{title}</h3>
                <p className="text-gray-300">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Preguntas frecuentes */}
      <section className="py-16 px-4 bg-black" aria-labelledby="faq">
        <div className="max-w-4xl mx-auto">
          <h2 id="faq" className="text-3xl font-bold text-center mb-10">Preguntas frecuentes</h2>
          <div className="space-y-6">
            {FAQS.map(({ q, a }) => (
              <div key={q}>
                <h3 className="font-semibold text-lg">{q}</h3>
                <p className="text-gray-300">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Llamado final */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 to-black border-t border-orange-500/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Tus datos tienen respuestas. Empieza a escucharlas.</h2>
          <p className="text-gray-300 mb-8">Crea tu cuenta gratis y haz tu primera pregunta hoy mismo.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register" className={primaryCta}>Crear cuenta gratis</Link>
            <Link to="/layautapp" className={secondaryCta}>Ya tengo cuenta</Link>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Home;
